import { useMemo } from 'react';
import { AlertCircle, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { USER_FIELDS, STRINGS } from './constants';
import { applyMappings, validateMappedUsers, hasRequiredMappings, getMappingStats } from './utils';
import type { ParsedCsvData, ColumnMapping, UserField } from './types';

interface StepMappingProps {
  csvData: ParsedCsvData;
  mapping: ColumnMapping;
  onMappingChange: (mapping: ColumnMapping) => void;
  onNext: () => void;
  onBack: () => void;
}

export default function StepMapping({
  csvData,
  mapping,
  onMappingChange,
  onNext,
  onBack,
}: StepMappingProps) {
  const mappedUsers = useMemo(
    () => applyMappings(csvData.rows, mapping, csvData.headers),
    [csvData, mapping],
  );

  const validations = useMemo(() => validateMappedUsers(mappedUsers), [mappedUsers]);
  const stats = useMemo(() => getMappingStats(validations), [validations]);
  const requiredOk = hasRequiredMappings(mapping);
  const canContinue = requiredOk && stats.rowsWithErrors === 0;

  const handleFieldChange = (header: string, value: string) => {
    onMappingChange({
      ...mapping,
      [header]: value === '' ? null : (value as UserField),
    });
  };

  const usedFields = new Set(Object.values(mapping).filter(Boolean));

  const mappedHeaders = csvData.headers.filter((h) => mapping[h]);

  return (
    <div className="space-y-6">
      <div>
        <h4 className="text-sm font-medium text-gray-700 mb-3">{STRINGS.mappingTitle}</h4>
        <div className="space-y-2">
          {csvData.headers.map((header) => {
            const currentField = mapping[header];
            return (
              <div key={header} className="flex items-center gap-3 p-2 bg-gray-50 rounded-lg">
                <div className="w-1/3">
                  <span className="text-sm font-mono text-gray-800">{header}</span>
                </div>
                <span className="text-gray-400">→</span>
                <div className="w-1/3">
                  <select
                    value={currentField || ''}
                    onChange={(e) => handleFieldChange(header, e.target.value)}
                    className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  >
                    <option value="">{STRINGS.mappingIgnore}</option>
                    {USER_FIELDS.map((field) => (
                      <option
                        key={field.key}
                        value={field.key}
                        disabled={usedFields.has(field.key) && currentField !== field.key}
                      >
                        {field.label}
                        {field.required ? ' *' : ''}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="w-1/6 text-xs">
                  {currentField && USER_FIELDS.find((f) => f.key === currentField)?.required && (
                    <span className="text-green-600 flex items-center gap-1">
                      <CheckCircle2 className="h-3 w-3" />
                      {STRINGS.mappingRequired}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {!requiredOk && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          <span>
            {STRINGS.requiredFieldsMissing}: Email, Emri, Mbiemri
          </span>
        </div>
      )}

      {requiredOk && (stats.totalErrors > 0 || stats.totalWarnings > 0) && (
        <div
          className={`flex items-center gap-2 p-3 border rounded-lg text-sm ${
            stats.totalErrors > 0
              ? 'bg-red-50 border-red-200 text-red-700'
              : 'bg-yellow-50 border-yellow-200 text-yellow-700'
          }`}
        >
          {stats.totalErrors > 0 ? (
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
          ) : (
            <AlertTriangle className="h-4 w-4 flex-shrink-0" />
          )}
          <span>
            {stats.totalErrors > 0 && `${stats.rowsWithErrors} rreshta me ${STRINGS.errors}. `}
            {stats.totalWarnings > 0 && `${stats.totalWarnings} ${STRINGS.warnings}.`}
          </span>
        </div>
      )}

      {requiredOk && mappedHeaders.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-2">
            {STRINGS.previewTitle} (te dhena te lidhura)
          </h4>
          <div className="overflow-x-auto border border-gray-200 rounded-lg">
            <table className="min-w-full text-xs">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-3 py-2 text-left font-medium text-gray-600">#</th>
                  {mappedHeaders.map((h) => (
                    <th key={h} className="px-3 py-2 text-left font-medium text-gray-600">
                      {USER_FIELDS.find((f) => f.key === mapping[h])?.label || h}
                    </th>
                  ))}
                  <th className="px-3 py-2 text-left font-medium text-gray-600">Status</th>
                </tr>
              </thead>
              <tbody>
                {mappedUsers.slice(0, 5).map((user, i) => {
                  const validation = validations[i];
                  const hasErrors = validation?.errors.length > 0;
                  const hasWarnings = validation?.warnings.length > 0;
                  return (
                    <tr
                      key={i}
                      className={`border-t border-gray-100 ${hasErrors ? 'bg-red-50' : hasWarnings ? 'bg-yellow-50' : ''}`}
                    >
                      <td className="px-3 py-2 text-gray-500">{i + 1}</td>
                      {mappedHeaders.map((h) => {
                        const field = mapping[h]!;
                        const value = user[field as keyof typeof user];
                        return (
                          <td key={h} className="px-3 py-2 text-gray-700 whitespace-nowrap">
                            {(value as string) || <span className="text-gray-300">-</span>}
                          </td>
                        );
                      })}
                      <td className="px-3 py-2">
                        {hasErrors ? (
                          <span className="text-red-600 text-xs">
                            {validation.errors.join('; ')}
                          </span>
                        ) : hasWarnings ? (
                          <span className="text-yellow-600 text-xs">
                            {validation.warnings.join('; ')}
                          </span>
                        ) : (
                          <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {mappedUsers.length > 5 && (
            <p className="text-xs text-gray-500 mt-1">
              ... dhe {mappedUsers.length - 5} rreshta te tjere
            </p>
          )}
        </div>
      )}

      <div className="flex justify-between">
        <button
          onClick={onBack}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
        >
          {STRINGS.btnBack}
        </button>
        <button
          onClick={onNext}
          disabled={!canContinue}
          className="px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {STRINGS.btnContinue}
        </button>
      </div>
    </div>
  );
}
